<template>
  <div class="lab-timeline" role="group" aria-label="Test timeline">
    <div class="tl-controls">
      <button type="button" class="tl-step pointer" aria-label="Previous reading" :disabled="index <= 0" @click="$emit('seek', index - 1)">◀</button>
      <button type="button" class="tl-step pointer" aria-label="Next reading" :disabled="index >= count - 1" @click="$emit('seek', index + 1)">▶</button>
      <div class="tl-now">
        <span class="tl-time">{{ current }}</span>
        <span class="tl-pos">{{ (index + 1).toLocaleString() }} / {{ count.toLocaleString() }}</span>
      </div>
    </div>

    <div class="tl-track">
      <span class="tl-edge">{{ start }}</span>
      <div class="tl-rail">
        <svg class="tl-spark" viewBox="0 0 1000 40" preserveAspectRatio="none" aria-hidden="true">
          <path :d="sparkPath" />
        </svg>
        <input
          type="range"
          min="0"
          :max="Math.max(0, count - 1)"
          step="1"
          :value="index"
          aria-label="Time in the test"
          :aria-valuetext="current"
          @input="$emit('seek', Number($event.target.value))"
        />
      </div>
      <span class="tl-edge">{{ end }}</span>
    </div>

    <button
      v-if="streaming"
      type="button"
      :class="['tl-live', 'pointer', { on: following }]"
      :aria-pressed="following"
      @click="$emit('live')"
    ><span class="dot" aria-hidden="true"></span>{{ following ? 'Following live' : 'Back to live' }}</button>
  </div>
</template>

<script>
// The dashboard's time cursor: one reading of the test, shared by the chart
// (a line at that time), the values panel and the Readings view. Dragging,
// the arrow keys or ◀ ▶ move it a reading at a time. While a test streams,
// the cursor follows the newest reading until it is moved back.
export default {
  name: 'LabTimeline',
  props: {
    count: { type: Number, required: true },
    index: { type: Number, required: true },
    // Clock times of the first reading, the last, and the cursor's
    start: { type: String, default: '' },
    end: { type: String, default: '' },
    current: { type: String, default: '' },
    following: { type: Boolean, default: true },
    // Readings still arriving: offers following the newest one
    streaming: { type: Boolean, default: false },
    // One channel across the test (nulls allowed), drawn behind the slider
    // so the ramp-up, trips and steady state are visible while scrubbing
    spark: { type: Array, default: () => [] }
  },
  computed: {
    sparkPath () {
      const values = this.spark
      const numbers = values.filter(v => v !== null)
      if (numbers.length < 2) return ''
      const min = Math.min(...numbers)
      const range = Math.max(...numbers) - min || 1
      const last = values.length - 1 || 1
      let d = ''
      let pen = false
      values.forEach((v, i) => {
        if (v === null) {
          pen = false
          return
        }
        const x = ((i / last) * 1000).toFixed(1)
        const y = (38 - ((v - min) / range) * 36).toFixed(1)
        d += `${pen ? 'L' : 'M'}${x} ${y}`
        pen = true
      })
      return d
    }
  }
}
</script>

<style scoped lang="scss">
// The thumb is 14px wide and travels its own width short of the rail; the
// trace is inset by half of it so the thumb sits on the time it names.
$thumb: 14px;
$thumb-half: 7px;

.lab-timeline {
  position: sticky;
  bottom: 0;
  z-index: 2;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 8px 20px;
  background: var(--lab-surface);
  border-top: 1px solid var(--lab-border);
  box-shadow: 0 -2px 8px rgba(15, 23, 42, 0.04);
}

.tl-controls {
  display: flex;
  align-items: center;
  gap: 4px;
  flex-shrink: 0;
}

.tl-step {
  width: 30px;
  height: 30px;
  font-size: 11px;
  color: var(--lab-accent);
  background: var(--lab-surface);
  border: 1px solid var(--lab-border);
  border-radius: 4px;

  &:hover:not(:disabled) { background: #eef1f5; }
  &:disabled { opacity: 0.35; cursor: default; }
  &:focus-visible { outline: 2px solid #60a5fa; outline-offset: 1px; }
}

.tl-now {
  display: flex;
  flex-direction: column;
  margin-left: 8px;
  min-width: 92px;
  line-height: 1.15;

  .tl-time {
    font-size: 15px;
    font-weight: 700;
    color: var(--lab-accent);
    font-variant-numeric: tabular-nums;
  }

  .tl-pos {
    font-size: 10px;
    color: var(--lab-muted);
    font-variant-numeric: tabular-nums;
  }
}

.tl-track {
  flex: 1;
  min-width: 0;
  display: flex;
  align-items: center;
  gap: 10px;
}

.tl-edge {
  font-size: 10px;
  color: var(--lab-muted);
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
}

.tl-rail {
  position: relative;
  flex: 1;
  height: 40px;
  border-radius: 4px;
  background: #f5f7fa;
}

.tl-spark {
  position: absolute;
  top: 0;
  bottom: 0;
  left: $thumb-half;
  width: calc(100% - #{$thumb});
  height: 100%;

  path {
    fill: none;
    stroke: #94a3b8;
    stroke-width: 1.5;
    vector-effect: non-scaling-stroke;
  }
}

input[type='range'] {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  margin: 0;
  background: transparent;
  cursor: pointer;
  -webkit-appearance: none;
  appearance: none;

  &::-webkit-slider-runnable-track { height: 100%; background: transparent; }
  &::-moz-range-track { height: 100%; background: transparent; }

  &::-webkit-slider-thumb {
    -webkit-appearance: none;
    width: $thumb;
    height: 40px;
    border-radius: 3px;
    background: rgba(30, 58, 95, 0.18);
    border: 2px solid var(--lab-accent);
  }

  &::-moz-range-thumb {
    width: $thumb - 4px;
    height: 36px;
    border-radius: 3px;
    background: rgba(30, 58, 95, 0.18);
    border: 2px solid var(--lab-accent);
  }

  &:focus-visible { outline: 2px solid #60a5fa; outline-offset: 2px; border-radius: 4px; }
}

.tl-live {
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  font-size: 11px;
  font-weight: 600;
  color: var(--lab-live);
  background: var(--lab-surface);
  border: 1px solid var(--lab-live);
  border-radius: 14px;
  white-space: nowrap;

  .dot {
    width: 7px;
    height: 7px;
    border-radius: 50%;
    background: currentColor;
  }

  &.on { color: #fff; background: var(--lab-live); }
  &:focus-visible { outline: 2px solid #60a5fa; outline-offset: 2px; }
}

// Phones: the rail takes a row of its own so it is wide enough to scrub
@media (max-width: 640px) {
  .lab-timeline {
    flex-wrap: wrap;
    gap: 8px 10px;
    padding: 8px 12px calc(8px + env(safe-area-inset-bottom));
  }

  .tl-track {
    order: -1;
    flex-basis: 100%;
  }

  .tl-step {
    width: 40px;
    height: 36px;
  }

  .tl-live { margin-left: auto; }
}
</style>
